import { useEffect, useState } from 'react';
import { authzApi, documentApi, projectApi, userprofileApi } from '@core/api';
import { useI18n } from '@react/hooks';
import { UserPicker } from '@/components/UserPicker';
import { candidateFromProfile } from '@/features/userprofile/pickerCandidates';

export interface AckRecipient { id: string; name: string }

async function listProfileIds(object: string, relation: string): Promise<Set<string>> {
  const ids = new Set<string>();
  let continuation_token = '';
  do {
    const response = await authzApi.ListUsersByObject({
      object, relation, page_size: 100,
      continuation_token: continuation_token || undefined,
      user_filters: { items: [{ type: 'profile', relation: '' }] },
    });
    for (const entry of response.users?.items ?? []) {
      const id = entry.user_id?.replace(/^profile:/, '');
      if (id) ids.add(id);
    }
    continuation_token = response.continuation_token || '';
  } while (continuation_token);
  return ids;
}

async function parentTeam(documentId: string): Promise<string> {
  let parent = (await documentApi.GetDocument(documentId)).document?.parent;
  const seen = new Set<string>();
  while (parent?.id && !seen.has(`${parent.type_name}:${parent.id}`)) {
    seen.add(`${parent.type_name}:${parent.id}`);
    if (parent.type_name === 'team') return parent.id;
    if (parent.type_name === 'project') parent = (await projectApi.GetProject(parent.id)).project?.parent;
    else if (parent.type_name === 'document') parent = (await documentApi.GetDocument(parent.id)).document?.parent;
    else break;
  }
  return '';
}

async function teamMembers(teamId: string, documentId: string): Promise<AckRecipient[]> {
  const [members, viewers] = await Promise.all([
    listProfileIds(`team:${teamId}`, 'member'),
    listProfileIds(`document:${documentId}`, 'viewer'),
  ]);
  const result: AckRecipient[] = [];
  const all = [...members].filter((id) => viewers.has(id));
  for (let i = 0; i < all.length; i += 100) {
    const response = await userprofileApi.ListUserprofilesByIDs({ ids: all.slice(i, i + 100) });
    for (const profile of response.userprofiles?.profiles ?? []) {
      const candidate = candidateFromProfile(profile);
      if (candidate.profileId) result.push({ id: candidate.profileId, name: candidate.displayName });
    }
  }
  return result.sort((a, b) => a.name.localeCompare(b.name));
}

export function AcknowledgmentRecipients({ documentId, value, onChange }: {
  documentId: string;
  value: AckRecipient[];
  onChange: (recipients: AckRecipient[]) => void;
}) {
  const { t } = useI18n();
  const [members, setMembers] = useState<AckRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!documentId) return;
    let cancelled = false;
    setMembers([]);
    setError(false);
    setLoading(true);
    parentTeam(documentId).then(async (teamId) => {
      if (!teamId) return [];
      return teamMembers(teamId, documentId);
    }).then((people) => { if (!cancelled) setMembers(people); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [documentId]);

  return <div className="space-y-2">
    <div className="text-sm font-medium text-foreground">{t('document.ackRecipients')}</div>
    {members.length > 0 && <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted"
      onClick={() => onChange([...members])}>{t('document.ackSelectWholeTeam', { count: members.length })}</button>}
    {loading && <div className="text-xs text-muted-foreground">{t('common.loading')}</div>}
    {error && <div className="text-xs text-destructive">{t('document.ackTeamLoadError')}</div>}
    <UserPicker value="" onChange={(id, name) => {
      if (id && !value.some((recipient) => recipient.id === id)) onChange([...value, { id, name }]);
    }} scope={{ typeName: 'document', id: documentId }} restrictToScope valueKind="profile"
      selectedIds={value.map((recipient) => recipient.id)} placeholder={t('document.ackActorPlaceholder')} />
    {value.length > 0 && <div className="flex flex-wrap gap-1.5" aria-label={t('document.ackSelectedRecipients')}>
      {value.map((recipient) => <button key={recipient.id} type="button" className="rounded-full bg-muted px-2.5 py-1 text-xs text-foreground hover:bg-muted/70"
        onClick={() => onChange(value.filter((item) => item.id !== recipient.id))}
        title={t('document.ackRemoveRecipient', { name: recipient.name })}>{recipient.name} ×</button>)}
    </div>}
    <div className="text-xs text-muted-foreground">{t('document.ackSelectedCount', { count: value.length })}</div>
  </div>;
}
